import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { Z_INDEX, zStyle } from '../../lib/zLayers';

export type UserAccountMenuPlacement = 'topbar' | 'sidebar';

interface UserAccountMenuProps {
  /** Where the trigger lives — controls dropdown direction and trigger styling. */
  placement?: UserAccountMenuPlacement;
  /** Compact avatar-only trigger (e.g. collapsed sidebar). */
  collapsed?: boolean;
  /** Mobile/simple mode: only Sign out. */
  signOutOnly?: boolean;
  className?: string;
}

const UserAccountMenu: React.FC<UserAccountMenuProps> = ({
  placement = 'topbar',
  collapsed = false,
  signOutOnly = false,
  className,
}) => {
  const { user, logout } = useAuth();
  const { colors } = useTheme();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const displayName =
    user?.userDisplayName?.trim() ||
    [user?.userFirstName, user?.userLastName].filter(Boolean).join(' ') ||
    user?.userEmail ||
    '';
  const userInitial = displayName ? displayName.charAt(0).toUpperCase() : 'U';
  const isSidebar = placement === 'sidebar';

  const close = () => setOpen(false);

  const handleLogout = async () => {
    close();
    await logout();
  };

  const handleSettings = () => {
    close();
    navigate('/profile-settings');
  };

  if (!user && isSidebar) return null;

  return (
    <div className={cn('relative', className)}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        className={cn(
          'flex w-full items-center gap-3 rounded-xl text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500/50',
          isSidebar
            ? cn(
                'px-2 py-2',
                collapsed && 'justify-center px-0',
                'hover:bg-white/10',
                open && 'bg-white/10'
              )
            : 'space-x-2 p-1.5 rounded-lg bg-transparent hover:bg-black/5 dark:hover:bg-white/5'
        )}
      >
        <div
          className={cn(
            'flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-teal-600/90',
            isSidebar ? 'ring-2 ring-white/10' : 'ring-2 ring-teal-600/20'
          )}
        >
          <span className="text-xs font-semibold text-white">{userInitial}</span>
        </div>

        {isSidebar && !collapsed && (
          <div className="min-w-0 flex-1">
            <p className={cn('truncate text-sm font-medium', colors.sidebarTextHover)}>
              {displayName || 'User'}
            </p>
            <p className={cn('truncate text-xs', colors.sidebarText)}>{user?.userEmail}</p>
          </div>
        )}

        {!isSidebar && (
          <svg
            className={cn('h-3.5 w-3.5 flex-shrink-0', colors.textMuted)}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        )}

        {isSidebar && !collapsed && (
          <svg
            className={cn('h-4 w-4 flex-shrink-0 opacity-70', colors.sidebarText)}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d={open ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'}
            />
          </svg>
        )}
      </button>

      {open && (
        <>
          <div
            className="fixed inset-0"
            style={{ zIndex: Z_INDEX.accountPopover - 1 }}
            onClick={close}
            aria-hidden
          />
          <div
            role="menu"
            className={cn(
              'absolute w-56 overflow-hidden rounded-xl border shadow-lift',
              'animate-in fade-in duration-150',
              colors.surface,
              colors.border,
              isSidebar
                ? collapsed
                  ? 'left-full bottom-0 ml-2'
                  : 'bottom-full left-0 right-0 mb-2 w-auto min-w-[14rem]'
                : 'right-0 top-full mt-2'
            )}
            style={zStyle(Z_INDEX.accountPopover)}
          >
            {!signOutOnly && (
              <div className={cn('border-b px-3.5 py-3', colors.border)}>
                <div className="flex items-center gap-2.5">
                  <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-teal-600/90">
                    <span className="text-sm font-semibold text-white">{userInitial}</span>
                  </div>
                  <div className="min-w-0">
                    <p className={cn('truncate text-sm font-semibold', colors.text)}>
                      {displayName || 'User'}
                    </p>
                    <p className={cn('truncate text-xs', colors.textMuted)}>{user?.userEmail}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="p-1.5">
              {!signOutOnly && (
                <button
                  type="button"
                  role="menuitem"
                  onClick={handleSettings}
                  className={cn(
                    'flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm font-medium transition-colors',
                    colors.text,
                    colors.interactiveHover
                  )}
                >
                  <svg
                    className={cn('h-4 w-4 flex-shrink-0', colors.textMuted)}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  Settings
                </button>
              )}

              <button
                type="button"
                role="menuitem"
                onClick={handleLogout}
                className={cn(
                  'flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm font-medium transition-colors',
                  'text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30'
                )}
              >
                <svg className="h-4 w-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                  />
                </svg>
                Sign out
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default UserAccountMenu;
